import { useFacets, useProductFilters } from "@nosto/search-js/preact/hooks"
import TermsFacet from "./TermsFacet/TermsFacet"
import RangeFacet from "./RangeFacet/RangeFacet"
import SortAccordion from "./SortAccordion/SortAccordion"
import Icon from "@/elements/Icon/Icon"
import { SearchStatsFacet, SearchTermsFacet } from "@nosto/nosto-js/client"
import styles from "./FilterSidebar.module.css"
import Button from "@/elements/Button/Button"
import { cl } from "@nosto/search-js/utils"
import { useFilterSidebar } from "@/contexts/SidebarContext"
import { useHiddenFacetHandles } from "@/contexts/HiddenFacetsContext"
import { STATIC_HIDDEN_FACET_HANDLES, facetHandle } from "@/utils/facetVisibility"
import SelectedFilters from "../SelectedFilters/SelectedFilters"
import ClearFiltersButton from "./ClearFiltersButton/ClearFiltersButton"
import { useCallback, useEffect, useRef, useState } from "preact/hooks"

const CLOSE_ANIMATION_MS = 300

export default function FilterSidebar() {
  const { facets } = useFacets()
  const { filters } = useProductFilters()
  const { isOpen, setOpen } = useFilterSidebar()
  const hiddenFacetHandles = useHiddenFacetHandles()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const [closing, setClosing] = useState(false)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
      setClosing(false)
      if (!dialog.open) {
        dialog.showModal()
      }
      return
    }

    if (!dialog.open) return

    setClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      dialog.close()
      setClosing(false)
      closeTimerRef.current = null
    }, CLOSE_ANIMATION_MS)
  }, [isOpen])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleBackdropClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        setOpen(false)
      }
    },
    [setOpen]
  )

  if (facets?.length === 0) {
    return null
  }

  const visibleFacets = facets?.filter(facet => {
    const handle = facetHandle(facet.name)
    return !STATIC_HIDDEN_FACET_HANDLES.has(handle) && !hiddenFacetHandles.has(handle)
  })

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <dialog
      ref={dialogRef}
      className={cl(styles.dialog, closing && styles.closing)}
      onClose={handleClose}
      onClick={handleBackdropClick}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.title}>Filter &amp; Sort</h2>
          <button
            type="button"
            className={styles.close}
            onClick={() => setOpen(false)}
            aria-label="Close filters"
          >
            <span className={styles.closeLabel}>Close</span>
            <Icon name="close" className={styles.closeIcon} />
          </button>
        </div>
        <div className={styles.selectedFiltersBlock}>
          <SelectedFilters />
        </div>
        <div className={styles.filtersGroup}>
          <SortAccordion />
          <ul className={styles.facets}>
            {visibleFacets?.map(facet => {
              switch (facet.type) {
                case "terms":
                  return <TermsFacet key={facet.id} facet={facet as SearchTermsFacet} />
                case "stats":
                  return <RangeFacet key={facet.id} facet={facet as SearchStatsFacet} />
                default:
                  return null
              }
            })}
          </ul>
        </div>
        <div className={styles.footer}>
          {filters.length ? (
            <div className={styles.clearFilters}>
              <ClearFiltersButton />
            </div>
          ) : null}
          <Button onClick={() => setOpen(false)} className={styles.viewResults} type="button">
            View Results
          </Button>
        </div>
      </div>
    </dialog>
  )
}
